import { useEffect, useMemo, useState } from "react";
import { Button, Typography } from "@material-tailwind/react";
import AsyncSelect from "react-select/async";
import toast from "react-hot-toast";
import { t } from "i18next";
import { api } from "../../../../services/api/api";
import { showApiErrorToast } from "../../../../services/api/error";
import { createTextSearchQuery, searchWithFilters } from "../../../../services/api/search";
import { setSelectList } from "../../../../services/utils/utils";
import { useDrawerLayoutDispatch } from "../../../../components/drawer-layout/DrawerLayout";
import AdminCustomerDisplay, { emptyRecordedData } from "./AdminCustomerDisplay";
import AdminCustomersTable from "./AdminCustomersTable";
import "./MergeCustomersDrawer.scss";

type SelectOption = EasyTech.IFormData<EasyTech.Customer>;

function customerLabel(customer: EasyTech.Customer): string {
  return customer.nip ? `${customer.name} - ${customer.nip}` : customer.name;
}

async function loadRecordedData(customers: EasyTech.Customer[]): Promise<EasyTech.Customer[]> {
  const ids = customers.map((c) => c.id).filter(Boolean);
  if (ids.length === 0) return customers;
  const recorded = await api.get(`admin/customers/recorded-data?ids=${ids.join(",")}`) as Record<string, EasyTech.CustomerRecordedData>;
  return customers.map((customer) => ({
    ...customer,
    recordedData: recorded[customer.id] || emptyRecordedData()
  }));
}

export default function MergeCustomersDrawer({
  onMerged
}: {
  onMerged: () => void
}) {
  const dispatch = useDrawerLayoutDispatch();
  const [mainCustomer, setMainCustomer] = useState<EasyTech.Customer | null>(null);
  const [sources, setSources] = useState<EasyTech.Customer[]>([]);
  const [pendingSource, setPendingSource] = useState<EasyTech.Customer | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const excludedIds = useMemo(() => {
    const ids = new Set<string>();
    if (mainCustomer?.id) ids.add(mainCustomer.id);
    sources.forEach((c) => ids.add(c.id));
    return ids;
  }, [mainCustomer, sources]);

  const loadCustomers = (inputValue: string, extraExcluded: Set<string>): Promise<SelectOption[]> => {
    return new Promise((resolve) => {
      const filterOut = (list: EasyTech.Customer[]) =>
        list.filter((el) => !extraExcluded.has(el.id)).sort((a, b) => a.name.localeCompare(b.name));

      if (inputValue && inputValue.length > 0) {
        searchWithFilters<EasyTech.Customer>("customers", {
          paginate: { pageSize: 100, pageNumber: 0 },
          queries: [createTextSearchQuery(["name", "nip"], inputValue)]
        }, false, true).then((response) => {
          resolve(setSelectList(filterOut(response.data), customerLabel));
        }).catch(() => resolve([]));
      } else {
        api.get("admin/customers/search").then((ret) => {
          resolve(setSelectList(filterOut(ret as EasyTech.Customer[]), customerLabel));
        }).catch(() => resolve([]));
      }
    });
  };

  const selectMain = async (customer?: EasyTech.Customer) => {
    if (!customer) {
      setMainCustomer(null);
      return;
    }
    const [withData] = await loadRecordedData([customer]);
    setMainCustomer(withData);
    setSources((prev) => prev.filter((c) => c.id !== customer.id));
  };

  const addSource = async () => {
    if (!pendingSource) return;
    if (excludedIds.has(pendingSource.id)) {
      toast.error(t("admin:customers.errors.alreadyAdded"));
      return;
    }
    const [withData] = await loadRecordedData([pendingSource]);
    setSources((prev) => [...prev, withData]);
    setPendingSource(null);
  };

  const closeDrawer = () => dispatch({ type: "close" });

  const submitMerge = () => {
    if (!mainCustomer) {
      toast.error(t("admin:customers.errors.noMain"));
      return;
    }
    if (sources.length === 0) {
      toast.error(t("admin:customers.errors.noSources"));
      return;
    }

    setSubmitting(true);
    api.post("admin/customers/merge", {
      mainCustomerId: mainCustomer.id,
      sourceCustomerIds: sources.map((c) => c.id)
    }).then(() => {
      toast.success(t("admin:customers.messages.mergeSuccess"));
      onMerged();
      closeDrawer();
    }).catch((err) => {
      showApiErrorToast(err, { fallbackMessage: t("admin:customers.errors.merge") });
    }).finally(() => setSubmitting(false));
  };

  useEffect(() => {
    setPendingSource(null);
  }, [mainCustomer?.id]);

  return (
    <div className="drawer-form merge-customers-drawer">
      <div className="content">
        <div className="merge-disclaimer">
          <Typography variant="h6" className="uppercase">
            {t("admin:customers.disclaimer.title")}
          </Typography>
          <Typography variant="small" className="mt-1">
            {t("admin:customers.disclaimer.intro")}
          </Typography>
          <ul>
            <li>{t("admin:customers.disclaimer.onlyOne")}</li>
            <li>{t("admin:customers.disclaimer.dataMoved")}</li>
            <li>{t("admin:customers.disclaimer.infoKept")}</li>
          </ul>
        </div>

        <section className="mt-6">
          <Typography variant="h6" className="mb-3">
            {t("admin:customers.mainCustomer")}
          </Typography>
          <div className="flex flex-col gap-2">
            <Typography variant="small" className="font-medium text-blue-gray-700">
              {t("admin:customers.customerToKeep")} *
            </Typography>
            <AsyncSelect
              cacheOptions
              defaultOptions
              loadOptions={(input) => loadCustomers(input, new Set(sources.map((c) => c.id)))}
              value={mainCustomer ? { value: mainCustomer, label: customerLabel(mainCustomer) } : null}
              onChange={(option) => selectMain(option?.value)}
              placeholder={t("admin:customers.searchPlaceholder")}
              classNamePrefix="react-select"
              isClearable
              theme={(theme) => ({
                ...theme,
                colors: { ...theme.colors, primary: "rgb(14 165 233)" }
              })}
            />
          </div>
          {mainCustomer && (
            <div className="mt-4">
              <AdminCustomerDisplay customer={mainCustomer} />
            </div>
          )}
        </section>

        <section className="mt-8">
          <Typography variant="h6" className="mb-3">
            {t("admin:customers.customersToMerge")}
          </Typography>
          <div className="add-customer-box">
            <Typography variant="small" className="mb-2 font-medium text-blue-gray-700">
              {t("admin:customers.addCustomer")}
            </Typography>
            <div>
              <AsyncSelect
                key={mainCustomer?.id || "no-main"}
                cacheOptions
                defaultOptions
                loadOptions={(input) => loadCustomers(input, excludedIds)}
                value={pendingSource ? { value: pendingSource, label: customerLabel(pendingSource) } : null}
                onChange={(option) => setPendingSource(option?.value || null)}
                placeholder={t("admin:customers.addPlaceholder")}
                classNamePrefix="react-select"
                isClearable
                theme={(theme) => ({
                  ...theme,
                  colors: { ...theme.colors, primary: "rgb(14 165 233)" }
                })}
              />
            </div>
            <div className="mt-3 flex justify-end">
              <Button size="sm" color="blue" onClick={addSource} disabled={!pendingSource}>
                {t("admin:customers.add")}
              </Button>
            </div>
          </div>

          {sources.length > 0 && (
            <div className="mt-4">
              <AdminCustomersTable
                customers={sources}
                onRemove={(customer) => setSources((prev) => prev.filter((c) => c.id !== customer.id))}
                enableRowLinks={false}
              />
            </div>
          )}
        </section>
      </div>

      <div className="footer">
        <Button size="sm" variant="outlined" onClick={closeDrawer} disabled={submitting}>
          {t("common:cancel")}
        </Button>
        <Button
          size="sm"
          color="red"
          onClick={submitMerge}
          disabled={submitting || !mainCustomer || sources.length === 0}
        >
          {t("common:validate")}
        </Button>
      </div>
    </div>
  );
}
